/**
 * Which of a call card's answers still apply (BAR-2918).
 *
 * A section can stop applying after the navigator has answered it: the
 * procedure changes from ESG to IGB and the employer section grays out, or
 * employment changes from "Benefits through a family member" to "Full-time" and
 * the family member's name is no longer asked. Those answers stay on the draft,
 * so changing back restores them, but they leave the saved summary and every
 * action taken from the card. What the card already did (a coverage button's
 * record) is not an answer and is never left out. One rule for every gated section, so the
 * insurance section (BAR-2919) reuses it rather than writing its own.
 */

import { Patient } from '@medplum/fhirtypes';
import { PacBeat, PacBeatAnswers, PacBeatContext, PacWidget } from './patient-access-script';

/** The `values` keys a widget writes. Most write one; a few write a companion key beside it. */
function answerIds(widget: PacWidget): string[] {
  switch (widget.kind) {
    case 'locationSelect':
      return [widget.id, `${widget.id}Reference`];
    case 'employerSearch':
      return [widget.id, widget.resultId];
    case 'insurerSelect':
      return [widget.id, `${widget.id}Reference`, `${widget.id}Key`];
    case 'insuranceEligibility':
      return [widget.id, `${widget.id}Eligible`];
    // A record of something done — a track switch, a task — not an answer.
    // It stays in the summary even when its section stops applying: the
    // action happened and can't be taken back by changing an answer.
    case 'verificationTask':
      return [];
    default:
      return 'id' in widget ? [widget.id] : [];
  }
}

function collect(
  items: readonly PacWidget[],
  answers: PacBeatAnswers,
  context: PacBeatContext | undefined,
  applies: boolean,
  applicable: Set<string>,
  inapplicable: Set<string>
): void {
  for (const widget of items) {
    if (widget.kind === 'group') {
      const shown = applies && (widget.when?.(answers, context) ?? true);
      collect(widget.items, answers, context, shown, applicable, inapplicable);
      continue;
    }
    for (const id of answerIds(widget)) {
      (applies ? applicable : inapplicable).add(id);
    }
  }
}

/**
 * The answers with every value that no longer applies left out: the values of
 * a beat whose `disabledReason` returns a reason, and of a group whose `when`
 * is false. A key that some applicable widget also writes is kept.
 *
 * Only `values` is filtered. Answers kept outside it (the procedure-date grid's
 * `procedureDate`) belong to beats that are never gated.
 */
export function answersThatApply<T extends PacBeatAnswers>(
  script: readonly PacBeat[],
  answers: T,
  patient: Patient | undefined,
  context?: PacBeatContext
): T {
  // Rules are read against what is left, until nothing more drops: a group
  // whose `when` reads an answer that was itself dropped (an insurer under a
  // hidden coverage question) goes too. Each pass drops at least one key, so
  // this ends.
  let current = answers;
  for (;;) {
    const next = dropOnce(script, current, patient, context);
    if (next === current) return current;
    current = next;
  }
}

function dropOnce<T extends PacBeatAnswers>(
  script: readonly PacBeat[],
  answers: T,
  patient: Patient | undefined,
  context: PacBeatContext | undefined
): T {
  const applicable = new Set<string>();
  const inapplicable = new Set<string>();
  for (const beat of script) {
    const applies = !beat.disabledReason?.(answers, patient, context);
    collect(beat.items, answers, context, applies, applicable, inapplicable);
  }

  const dropped = [...inapplicable].filter((id) => !applicable.has(id) && answers.values[id] !== undefined);
  if (dropped.length === 0) return answers;

  const values = { ...answers.values };
  for (const id of dropped) delete values[id];
  return { ...answers, values };
}
